import { useState, useMemo, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Undo2, AlertTriangle, Package, Truck, Loader2 } from "lucide-react";
import { api, formatCOP, formatNumber } from "@/lib/api";
import { toast } from "sonner";

/**
 * Modal de devolución de pedido (mar/2026).
 *
 * El operador puede registrar que un cliente devolvió todo o parte de un
 * pedido. Elige qué ítems vuelven a bodega y si la empresa cubre los fletes.
 *
 * Efectos al confirmar:
 *   - Devuelve stock de los ítems seleccionados.
 *   - Recalcula totales y comisión del pedido.
 *   - Si es total: estado → devuelto, comisión = 0.
 *   - Si el cliente ya pagó: genera cupo a favor (crédito real).
 *
 * Props:
 *   - pedido: objeto pedido con items[]
 *   - open, onOpenChange
 *   - onDevuelto: callback tras devolución exitosa (para refrescar la vista)
 */
export default function DevolucionPedidoModal({ pedido, open, onOpenChange, onDevuelto }) {
  const [razon, setRazon] = useState("");
  const [categoria, setCategoria] = useState("");
  const [categorias, setCategorias] = useState([]);
  const [seleccion, setSeleccion] = useState({});  // { item_id: cantidad_a_devolver }
  const [valorFleteVuelta, setValorFleteVuelta] = useState("");
  const [enviando, setEnviando] = useState(false);

  const items = pedido?.items || [];
  const fleteCliente = pedido?.flete_cliente || 0;
  const totalPagado = pedido?.total_pagado || 0;

  // Cargar categorías cuando se abre el modal
  useEffect(() => {
    if (!open) return;
    api.get("/pedidos/devoluciones/categorias")
      .then(({ data }) => setCategorias(data.categorias || []))
      .catch(() => setCategorias([]));
  }, [open]);

  // Derivar comportamiento automático de fletes según categoría
  const empresaPagaFleteVuelta = categoria && categoria !== "cambio_cliente";
  const cantSeleccionada = Object.values(seleccion).reduce((a, b) => a + (parseFloat(b) || 0), 0);
  const cantTotalItems = items.reduce((a, i) => a + (parseFloat(i.cantidad) || 0), 0);
  const esTotal = cantSeleccionada > 0 && cantSeleccionada >= cantTotalItems &&
                  Object.keys(seleccion).length === items.length;
  const empresaPagaFleteIda = esTotal && fleteCliente > 0;

  const resumen = useMemo(() => {
    let cant = 0;
    let subtotal = 0;
    Object.entries(seleccion).forEach(([itemId, qty]) => {
      const it = items.find((i) => i.id === itemId);
      if (!it || !qty) return;
      const q = parseFloat(qty) || 0;
      if (q <= 0) return;
      cant += q;
      subtotal += Math.round((it.precio_unitario || 0) * q);
    });
    return { cant, subtotal };
  }, [seleccion, items]);

  const toggleTodo = (item) => {
    setSeleccion((s) => {
      if (s[item.id]) {
        const copy = { ...s };
        delete copy[item.id];
        return copy;
      }
      return { ...s, [item.id]: item.cantidad };
    });
  };

  const setCantidad = (item, val) => {
    const q = Math.max(0, Math.min(parseFloat(val) || 0, item.cantidad));
    setSeleccion((s) => {
      if (q === 0) {
        const copy = { ...s };
        delete copy[item.id];
        return copy;
      }
      return { ...s, [item.id]: q };
    });
  };

  const confirmar = async () => {
    if (!categoria) { toast.error("Elige el motivo/categoría de la devolución"); return; }
    if (!razon.trim()) { toast.error("Escribe una descripción de la devolución"); return; }
    const itemsPayload = Object.entries(seleccion)
      .map(([item_id, cantidad]) => ({ item_id, cantidad: parseFloat(cantidad) || 0 }))
      .filter((x) => x.cantidad > 0);
    if (itemsPayload.length === 0) { toast.error("Elige al menos un ítem"); return; }
    if (empresaPagaFleteVuelta && !valorFleteVuelta) {
      toast.error("Ingresa el valor del flete de vuelta");
      return;
    }

    setEnviando(true);
    try {
      const payload = {
        razon: razon.trim(),
        categoria,
        items: itemsPayload,
        valor_flete_vuelta: parseInt(valorFleteVuelta) || 0,
        crear_cupo_por_pago: true,
      };
      const { data } = await api.post(`/pedidos/${pedido.id}/devolver`, payload);
      const msg = data.es_devolucion_total
        ? `Devolución total registrada. Stock devuelto${data.cupo_generado > 0 ? `. Cupo a favor del cliente: ${formatCOP(data.cupo_generado)}` : ""}.`
        : `Devolución parcial: ${data.items_devueltos} ítem(s) volvieron a bodega. Nuevo total: ${formatCOP(data.nuevo_total)}.`;
      toast.success(msg);
      onDevuelto?.(data);
      onOpenChange(false);
      setRazon(""); setSeleccion({}); setValorFleteVuelta(""); setCategoria("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar la devolución");
    } finally {
      setEnviando(false);
    }
  };

  if (!pedido) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="devolucion-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <Undo2 className="w-5 h-5 text-primary" />
            Devolución de pedido {pedido.numero || ""}
          </DialogTitle>
          <div className="text-xs text-muted-foreground">
            Cliente: <b className="text-foreground">{pedido.cliente_nombre}</b> · Total del pedido:{" "}
            <b className="text-foreground">{formatCOP(pedido.total)}</b>
            {totalPagado > 0 && <span> · Pagado: <b className="text-primary">{formatCOP(totalPagado)}</b></span>}
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Categoría (motivo estandarizado) */}
          <div className="space-y-1">
            <Label className="text-xs uppercase font-bold">Motivo de la devolución *</Label>
            <div className="grid grid-cols-2 gap-1.5" data-testid="devolucion-categorias">
              {categorias.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setCategoria(c.key)}
                  className={`text-left px-3 py-2 rounded-md text-xs border transition-all ${
                    categoria === c.key
                      ? "bg-primary text-primary-foreground border-primary shadow-sm"
                      : "bg-background border-border hover:border-primary/50 text-foreground"
                  }`}
                  data-testid={`devolucion-categoria-${c.key}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Razón (texto libre) */}
          <div className="space-y-1">
            <Label className="text-xs uppercase font-bold">Descripción *</Label>
            <Textarea
              value={razon}
              onChange={(e) => setRazon(e.target.value)}
              placeholder="Añade detalles: SKU con defecto, dirección incorrecta, etc."
              rows={2}
              className="text-sm"
              data-testid="devolucion-razon"
            />
          </div>

          {/* Items */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase font-bold flex items-center gap-1">
                <Package className="w-3 h-3" /> ¿Qué ítems se devuelven?
              </Label>
              <button
                onClick={() => {
                  const todos = {};
                  items.forEach((i) => { todos[i.id] = i.cantidad; });
                  setSeleccion(todos);
                }}
                className="text-[11px] text-primary hover:underline"
                data-testid="devolucion-todos-btn"
              >
                Seleccionar todos
              </button>
            </div>
            <div className="border border-border rounded-lg divide-y divide-border max-h-64 overflow-y-auto">
              {items.map((it) => {
                const isSelected = seleccion[it.id] !== undefined;
                const qty = seleccion[it.id] || 0;
                return (
                  <div key={it.id} className={`p-2 flex items-center gap-2 ${isSelected ? "bg-primary/5" : ""}`}>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleTodo(it)}
                      data-testid={`devolucion-check-${it.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">{it.producto_nombre}</div>
                      <div className="text-[10px] text-muted-foreground flex items-center gap-2">
                        <span>Código: {it.codigo || it.sku}</span>
                        <span>·</span>
                        <span>{formatNumber(it.cantidad)} unid × {formatCOP(it.precio_unitario)}</span>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          value={qty}
                          onChange={(e) => setCantidad(it, e.target.value)}
                          min={0}
                          max={it.cantidad}
                          step={1}
                          className="w-20 h-8 text-sm text-right"
                          data-testid={`devolucion-qty-${it.id}`}
                        />
                        <span className="text-[10px] text-muted-foreground">/ {formatNumber(it.cantidad)}</span>
                      </div>
                    )}
                  </div>
                );
              })}
              {items.length === 0 && (
                <div className="p-3 text-center text-xs italic text-muted-foreground">Pedido sin ítems</div>
              )}
            </div>
            {resumen.cant > 0 && (
              <div className="text-[11px] text-primary tabular-nums pt-1">
                {formatNumber(resumen.cant)} unid seleccionadas · Subtotal a devolver:{" "}
                <b>{formatCOP(resumen.subtotal)}</b>
              </div>
            )}
          </div>

          {/* Panel de fletes — automático según categoría y tipo (parcial/total) */}
          <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-2">
            <div className="text-xs font-semibold text-amber-900 flex items-center gap-1">
              <Truck className="w-4 h-4" /> Fletes (calculado automático)
            </div>

            {/* Flete de VUELTA */}
            <div className="space-y-1">
              <div className="text-sm text-foreground flex items-start gap-2">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase flex-shrink-0 ${
                  empresaPagaFleteVuelta ? "bg-rose-200 text-rose-900" : "bg-emerald-200 text-emerald-900"
                }`}>
                  {empresaPagaFleteVuelta ? "Absorbe empresa" : "Cobrado al cliente"}
                </span>
                <div className="flex-1 text-xs">
                  <b>Flete de vuelta</b> (transportadora → bodega).{" "}
                  {categoria === "cambio_cliente"
                    ? "El cliente pidió el cambio, se le cobra a él."
                    : categoria
                    ? "La empresa lo cubre porque el motivo no es cambio del cliente."
                    : "Elige un motivo arriba para ver quién lo paga."}
                </div>
              </div>
              {categoria && (
                <Input
                  type="number"
                  value={valorFleteVuelta}
                  onChange={(e) => setValorFleteVuelta(e.target.value)}
                  placeholder="Valor del flete de vuelta (COP)"
                  className="h-8 text-sm max-w-xs"
                  data-testid="devolucion-flete-vuelta-valor"
                />
              )}
            </div>

            {/* Flete de IDA — se absorbe solo en devolución total */}
            {fleteCliente > 0 && (
              <div className="text-sm text-foreground flex items-start gap-2 pt-1 border-t border-amber-200">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase flex-shrink-0 ${
                  empresaPagaFleteIda ? "bg-rose-200 text-rose-900" : "bg-emerald-200 text-emerald-900"
                }`}>
                  {empresaPagaFleteIda ? "Absorbe empresa" : "Cobrado al cliente"}
                </span>
                <div className="flex-1 text-xs">
                  <b>Flete de ida</b> ({formatCOP(fleteCliente)}) prestado al cliente.{" "}
                  {empresaPagaFleteIda
                    ? "El pedido no se entregó, la empresa absorbe la pérdida."
                    : "Como parte del pedido igual se entregó, se sigue cobrando al cliente."}
                </div>
              </div>
            )}
          </div>

          {/* Aviso: pedido sin pagos */}
          {totalPagado === 0 && resumen.subtotal > 0 && (
            <div className="text-xs bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-md p-2 text-slate-700 dark:text-slate-200 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                Este cliente no ha pagado nada, así que <b>no se generará cupo</b>. Solo se ajusta el
                pedido y el stock vuelve a bodega.
              </div>
            </div>
          )}

          {/* Aviso: cupo generado (cliente ya pagó) */}
          {totalPagado > 0 && resumen.subtotal > 0 && (
            <div className="text-xs bg-blue-50 border border-blue-200 rounded-md p-2 text-blue-900 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                Como el cliente ya pagó, se generará un <b>cupo a favor</b> del cliente
                (crédito real para futuros pedidos), distinto del cupo de endeudamiento.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 pt-3 border-t border-border">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={enviando}>
            Cancelar
          </Button>
          <Button
            onClick={confirmar}
            disabled={enviando || !razon.trim() || resumen.cant === 0}
            className="bg-primary hover:bg-primary/90"
            data-testid="devolucion-confirmar-btn"
          >
            {enviando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Undo2 className="w-4 h-4 mr-2" />}
            Registrar devolución
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
